export interface FixedBlock {
  day: number;      // 0=Mon … 6=Sun
  hour: number;     // 0–23
  half: 0 | 1;     // 0 = :00, 1 = :30
  label: string;
  /**
   * When true, the booking flow ignores this block if the booking covers
   * at least `PricingConfig.fullDayThreshold` hours (full-day booking).
   * Useful for "soft" blocks like cleaning slots that the owner is willing
   * to skip when the entire day is rented.
   */
  cancelableOnFullDay?: boolean;
}

/**
 * Seasonal recurring block — applies only between `startDate` and `endDate`
 * (both inclusive, 'YYYY-MM-DD'). Range-based, unlike FixedBlock which is
 * exploded per 30-min slot. Used for time-limited recurrences like
 * "Mondays 17:00–18:00 from October to June there's a yoga class".
 *
 * A FixedBlock without date bounds means "para siempre" — applies every
 * week, year-round, and may be overridden by a customer booking the entire
 * day (TBD in the booking flow).
 */
export interface SeasonalBlock {
  /** Stable id, generated client-side. */
  id:        string;
  /** 0=Mon … 6=Sun */
  day:       number;
  /** 'HH:MM' (inclusive, 30-min granularity) */
  startTime: string;
  /** 'HH:MM' (exclusive) */
  endTime:   string;
  /** 'YYYY-MM-DD' inclusive */
  startDate: string;
  /** 'YYYY-MM-DD' inclusive */
  endDate:   string;
  label:     string;
  /**
   * When true, the booking flow ignores this block if the booking covers
   * at least `PricingConfig.fullDayThreshold` hours (full-day booking).
   */
  cancelableOnFullDay?: boolean;
}

/** Per-day operating window — 'HH:MM' (30-min granularity). */
export interface DayHours {
  open:  string;
  close: string;
}

/**
 * Operating window for each day of the week. Index 0 = Monday, 6 = Sunday.
 * MUST contain exactly 7 entries. `openingTime`/`closingTime` on the Space
 * are kept in sync as the min-open / max-close envelope, so existing
 * consumers (calendar, schedule-step, user-booking) keep working without
 * having to know about per-day hours yet.
 */
export type WeeklyHours = DayHours[];

/** Default operating window when creating a brand-new space. */
export const DEFAULT_WEEKLY_HOURS: WeeklyHours = Array.from(
  { length: 7 },
  () => ({ open: '10:00', close: '22:00' }),
);

/** €/hour rate for each day of the week. */
export interface WeeklyRates {
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
  sun: number;
}

/** All configured day rates as a flat list (Mon → Sun). */
export function weeklyRateValues(rates: WeeklyRates): number[] {
  return [rates.mon, rates.tue, rates.wed, rates.thu, rates.fri, rates.sat, rates.sun];
}

/**
 * Lowest €/h rate for listing cards ("Desde X€/h"). Uses `pricing.weeklyRates`
 * when present; falls back to legacy `pricePerHour` for spaces without Step 3 data.
 */
export function minHourlyRate(space: Pick<Space, 'pricePerHour' | 'pricing'>): number {
  const rates = space.pricing?.weeklyRates;
  if (rates) {
    const positive = weeklyRateValues(rates).filter(v => v > 0);
    if (positive.length > 0) return Math.min(...positive);
  }
  return space.pricePerHour ?? 0;
}

/** Euro formatter for templates (es-ES, no Angular locale registration required). */
export function formatEuro(amount: number): string {
  return new Intl.NumberFormat('es-ES', {
    style:                 'currency',
    currency:              'EUR',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * One row in the volume-discount table. `min` is inclusive in hours, `max`
 * is exclusive. The last tier typically has `max` = 24 (open-ended). `pct`
 * is the percentage discount applied to the base hourly cost (0..100).
 */
export interface DiscountTier {
  min: number;
  max: number;
  pct: number;
}

/**
 * Pricing configuration for a space. Authored in Step 3 (Tarifas) of the
 * wizard. All amounts in euros unless noted.
 */
export interface PricingConfig {
  /** Flat fee added on top of every booking. */
  cleaningFee:            number;
  /** Floor on booking duration (hours). */
  minHours:               number;
  /**
   * Booking duration (hours) at or above which the day's standard shift
   * blocks are ignored — the customer effectively reserves "the full day".
   */
  fullDayThreshold:       number;
  /**
   * Per-day €/h rate. The Saturday rate applies on the eve of a holiday
   * and the Sunday rate on the holiday itself — the relationship is implicit
   * (no separate flag needed), applied when computing the booking price.
   */
  weeklyRates:            WeeklyRates;
  /** Volume discount tiers — see {@link DiscountTier}. */
  discountRules:          DiscountTier[];
}

/** Defaults seeded for a brand-new space's pricing block. */
export const DEFAULT_PRICING: PricingConfig = {
  cleaningFee:      0,
  minHours:         3,
  fullDayThreshold: 6,
  weeklyRates:      { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 },
  discountRules: [
    { min: 4, max: 5,  pct: 0 },
    { min: 5, max: 6,  pct: 0 },
    { min: 6, max: 24, pct: 0 },
  ],
};

/**
 * Spanish national holidays with fixed dates. Stored as 'MM-DD' so they
 * resolve to a concrete date once a year is picked. Variable-date holidays
 * (Viernes Santo, Lunes de Pascua, etc.) are intentionally excluded — add a
 * computed-Easter helper later if needed. Source: Boletín Oficial del Estado
 * — días inhábiles laborales a efectos nacionales.
 */
export const NATIONAL_HOLIDAYS_MMDD: readonly string[] = [
  '01-01', // Año Nuevo
  '01-06', // Epifanía del Señor (Reyes)
  '05-01', // Fiesta del Trabajo
  '08-15', // Asunción de la Virgen
  '10-12', // Fiesta Nacional de España
  '11-01', // Todos los Santos
  '12-06', // Día de la Constitución
  '12-08', // Inmaculada Concepción
  '12-25', // Navidad
];

const WEEKLY_RATE_KEYS: (keyof WeeklyRates)[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

function parseDateStr(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function dateStrAddDays(dateStr: string, days: number): string {
  const d = parseDateStr(dateStr);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dayOfWeekMon0(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/** Minimum booking duration (hours) configured for a space. */
export function minHoursForSpace(space: Pick<Space, 'pricing'>): number {
  return space.pricing?.minHours ?? DEFAULT_PRICING.minHours;
}

/** True when `dateStr` is a vacation closure date. */
export function isVacationDate(dateStr: string, space: Pick<Space, 'vacationDates'>): boolean {
  return (space.vacationDates ?? []).includes(dateStr);
}

/** True when `dateStr` is a national or owner-marked holiday. */
export function isHolidayDate(dateStr: string, space: Pick<Space, 'customHolidays'>): boolean {
  if ((space.customHolidays ?? []).includes(dateStr)) return true;
  return NATIONAL_HOLIDAYS_MMDD.includes(dateStr.slice(5));
}

/** True when the following calendar day is a holiday (eve → Saturday rate). */
export function isEveOfHoliday(dateStr: string, space: Pick<Space, 'customHolidays'>): boolean {
  return isHolidayDate(dateStrAddDays(dateStr, 1), space);
}

function effectiveHourlyRate(configured: number, fallback: number): number {
  return configured > 0 ? configured : fallback;
}

/** €/h for a concrete booking date (weekday / eve / holiday rules). */
export function hourlyRateForDate(
  space: Pick<Space, 'pricePerHour' | 'pricing' | 'customHolidays'>,
  dateStr: string,
): number {
  const pricing  = space.pricing ?? DEFAULT_PRICING;
  const rates    = pricing.weeklyRates;
  const fallback = space.pricePerHour ?? 0;
  if (isHolidayDate(dateStr, space)) {
    return effectiveHourlyRate(rates.sun, fallback);
  }
  if (isEveOfHoliday(dateStr, space)) {
    return effectiveHourlyRate(rates.sat, fallback);
  }
  const dow = dayOfWeekMon0(parseDateStr(dateStr));
  return effectiveHourlyRate(rates[WEEKLY_RATE_KEYS[dow]], fallback);
}

/** Total rental price for a single booked day (day rate + cleaning fee). */
export function quoteBookingDailyTotal(
  space: Pick<Space, 'pricePerHour' | 'pricing' | 'customHolidays'>,
  dateStr: string,
): number {
  const pricing = space.pricing ?? DEFAULT_PRICING;
  const rental  = hourlyRateForDate(space, dateStr);
  return Math.round((rental + pricing.cleaningFee) * 100) / 100;
}

/** Same as {@link quoteBookingDailyTotal}; range arguments are ignored by design. */
export function quoteBookingDailyTotalForRange(
  space: Pick<Space, 'pricePerHour' | 'pricing' | 'customHolidays'>,
  dateStr: string,
  _startTime: string,
  _endTime: string,
): number {
  return quoteBookingDailyTotal(space, dateStr);
}

/**
 * Booking length in hours from grid selection times (30-min slots; `endTime` exclusive).
 * Rounds to the nearest half-hour so totals stay aligned with the weekly grid.
 */
export function bookingDurationHours(startTime: string, endTime: string): number {
  const parseStart = (t: string): number => {
    const [h, m] = t.split(':').map(Number);
    return (h ?? 0) + (m ?? 0) / 60;
  };
  const parseEnd = (t: string): number => {
    if (t === '24:00') return 24;
    if (t === '00:00' && startTime && startTime !== '00:00') return 24;
    const [h, m] = t.split(':').map(Number);
    return (h ?? 0) + (m ?? 0) / 60;
  };
  const diff = parseEnd(endTime) - parseStart(startTime);
  if (diff <= 0) return 0;
  return Math.round(diff * 2) / 2;
}

/**
 * Rental subtotal for a booking: day rate × hours − volume discount + cleaning fee.
 * Excludes security deposit (paid separately when applicable).
 */
export function quoteBookingRentalTotal(
  space: Pick<Space, 'pricePerHour' | 'pricing' | 'customHolidays'>,
  dateStr: string,
  hours: number,
): number {
  if (hours <= 0) return 0;
  const pricing = space.pricing ?? DEFAULT_PRICING;
  const rate    = hourlyRateForDate(space, dateStr);
  const rental  = quoteRentalSubtotal(space, rate, hours);
  return Math.round((rental + pricing.cleaningFee) * 100) / 100;
}

/** Same as {@link quoteBookingRentalTotal} but derives hours from HH:MM range (30-min grid). */
export function quoteBookingRentalTotalForRange(
  space: Pick<Space, 'pricePerHour' | 'pricing' | 'customHolidays'>,
  dateStr: string,
  startTime: string,
  endTime: string,
): number {
  return quoteBookingRentalTotal(space, dateStr, bookingDurationHours(startTime, endTime));
}

/** Rental subtotal (hours × rate − volume discount), excluding cleaning fee. */
export function quoteRentalSubtotal(
  space: Pick<Space, 'pricePerHour' | 'pricing' | 'customHolidays'>,
  hourlyRate: number,
  hours: number,
): number {
  if (hours <= 0) return 0;
  const pricing  = space.pricing ?? DEFAULT_PRICING;
  const subtotal = hourlyRate * hours;
  const tier     = pricing.discountRules.find(r => hours >= r.min && hours < r.max);
  const discount = subtotal * (tier?.pct ?? 0) / 100;
  return Math.round(Math.max(0, subtotal - discount) * 100) / 100;
}

/**
 * Floor estimate for listing/detail cards: cheapest €/h × min hours (rental only).
 * Cleaning is optional and not included here. For a concrete booking date use
 * `quoteBookingRentalTotal`.
 */
export function quoteMinBookingTotal(
  space: Pick<Space, 'pricePerHour' | 'pricing' | 'customHolidays'>,
): number {
  const hours = minHoursForSpace(space);
  if (hours <= 0) return 0;
  return quoteRentalSubtotal(space, minHourlyRate(space), hours);
}

/**
 * Space category. Used for filtering in /explorar and for SEO metadata.
 * - `sala_fiestas`: Party halls, celebration rooms, banquet spaces.
 * - `estudio`:      Photography studios, recording studios, rehearsal rooms.
 * - `coworking`:    Meeting rooms, workshops, co-working spaces.
 * - `terraza`:      Rooftops, terraces, outdoor patios.
 * - `cocina`:       Equipped kitchens, showcooking, gastronomy events.
 * - `singular`:     Galleries, industrial warehouses, unique multipurpose venues.
 * - `otro`:         Legacy — deprecated, treated as `singular`.
 */
export type SpaceCategory =
  | 'sala_fiestas'
  | 'estudio'
  | 'coworking'
  | 'terraza'
  | 'cocina'
  | 'singular'
  | 'otro';

export const SPACE_CATEGORY_LABELS: Record<SpaceCategory, string> = {
  sala_fiestas: 'Celebraciones y Fiestas',
  estudio:      'Estudios y Set Creativo',
  coworking:    'Reuniones y Workshops',
  terraza:      'Terrazas y Áticos',
  cocina:       'Cocinas y Showcooking',
  singular:     'Espacios Singulares',
  otro:         'Espacio',
};

export const SPACE_CATEGORIES: SpaceCategory[] = [
  'sala_fiestas', 'estudio', 'coworking', 'terraza', 'cocina', 'singular',
];

/**
 * Returns the effective categories for a space.
 * Reads `categories[]` first; falls back to legacy `[category]`.
 */
export function getSpaceCategories(
  space: { categories?: SpaceCategory[]; category?: SpaceCategory },
): SpaceCategory[] {
  if (space.categories?.length) return space.categories;
  if (space.category && space.category !== 'otro') return [space.category];
  return ['singular'];
}

/** A gallery image with its full + thumbnail variants (both WebP, client-generated). */
export interface SpaceImage {
  id: string;
  urlFull: string;
  urlThumb: string;
}

/**
 * Cover thumbnail URL for list/card views — prefers the lighter `urlThumb`,
 * falling back to the full image (legacy spaces without structured `images`).
 * Returns '' when the space has no photos.
 */
export function spaceCoverThumb(space: Pick<Space, 'images' | 'imageUrls'>): string {
  const first = space.images?.[0];
  if (first) return first.urlThumb || first.urlFull;
  return space.imageUrls?.[0] ?? '';
}

export interface Space {
  id?: string;
  ownerId: string;
  name: string;
  description: string;
  address: string;
  /** Full formatted line from Places Autocomplete. */
  formattedAddress?: string;
  postalCode?: string;
  country?: string;
  countryCode?: string;
  province?: string;
  /** Barrio / distrito; falls back to city when sublocality is absent. */
  district?: string;
  slugProvincia?: string;
  slugDistrito?: string;
  /** SEO path: `madrid/el-canaveral/nombre-sala` (ES) or `pt/lisboa/...` abroad. */
  slugCompleto?: string;
  googlePlaceId?: string;
  latitude?: number;
  longitude?: number;
  /**
   * Multi-category support. Source of truth when present.
   * Use `getSpaceCategories(space)` to read — never access directly.
   */
  categories?: SpaceCategory[];
  /**
   * Primary category (first element of `categories`). Kept for Firestore
   * index backward-compatibility and legacy reads. Written on every save.
   * @deprecated Read via `getSpaceCategories(space)` instead.
   */
  category?: SpaceCategory;
  /**
   * Denormalized owner display name. Stamped from `auth.currentUser.displayName`
   * when the space is created or updated in step 1. Used for the public owner
   * profile page (/propietario/:slug) without needing to read the user doc.
   */
  ownerDisplayName?: string;
  /**
   * Denormalized owner slug — mirror of `users/{ownerId}.ownerSlug`. Lets
   * cards render `[routerLink]="['/propietario', space.ownerSlug]"` without
   * a second Firestore read per space. Kept in sync server-side by the
   * `assignOwnerSlugOnUserWrite` trigger and the `updateOwnerSlug` callable.
   * Empty string while the trigger hasn't fired yet — fall back to skipping
   * the link in that case.
   */
  ownerSlug?: string;
  /** Public contact phone (often from Google Places). */
  phone?: string;
  /** Business website URL. */
  website?: string;
  pricePerHour: number;
  /** Full-resolution image URLs (WebP ~1200px). Source of truth for legacy readers. */
  imageUrls: string[];
  /**
   * Structured gallery: each entry pairs the full image with its lighter
   * thumbnail (both generated client-side, WebP). `imageUrls` is kept in sync
   * (= images.map(urlFull)) so existing consumers keep working.
   */
  images?: SpaceImage[];
  capacity: number;
  cleaningBuffer: number;
  fixedBlocks?: FixedBlock[];
  /** Time-bounded recurring blocks. See SeasonalBlock. */
  seasonalBlocks?: SeasonalBlock[];
  /** Step 3 pricing block. See PricingConfig. */
  pricing?: PricingConfig;
  active?: boolean;
  featureIds?: string[];
  /** Envelope opening time — min(weeklyHours[*].open). Kept for legacy consumers. */
  openingTime?: string;  // "HH:MM", e.g. "10:00"
  /** Envelope closing time — max(weeklyHours[*].close). Kept for legacy consumers. */
  closingTime?: string;  // "HH:MM", e.g. "22:00"
  /** Per-day opening/closing window. Source of truth when present. */
  weeklyHours?: WeeklyHours;
  /**
   * Owner-marked holiday dates ('YYYY-MM-DD'). National holidays are derived
   * separately (see NATIONAL_HOLIDAYS_MMDD) and don't need to be repeated
   * here — this list is for ad-hoc closures (vacaciones, festivos locales,
   * cierre puntual). The Step 2 "Calendario" tab in the wizard mutates it.
   */
  customHolidays?: string[];
  /**
   * Vacation closure dates ('YYYY-MM-DD'). The space is fully closed on these
   * days — the booking calendar treats them as unavailable (same as a day
   * with openingTime == closingTime == '00:00'). Set via Step 2 of the wizard.
   */
  vacationDates?: string[];
  /**
   * Refundable security deposit charged on top of the rental price when the
   * owner is in STRIPE payout mode. Euros (not cents). Defaults to 0 when
   * missing. Never charged in MANUAL payout mode — the deposit, if any, is
   * collected by the owner directly at the venue.
   */
  securityDeposit?: number;
  /**
   * Conditions block set in Step 5 (Condiciones) of the wizard.
   * Contains cancellation policy, deposit method, cleaning rules, min age and house rules.
   */
  conditions?: SpaceConditions;
  /** Owner-editable HTML template used in digital lease contract generation. */
  contractTemplateHtml?: string;
  /** Denormalized from /reviews — maintained by onReviewAggregate. */
  averageRating?: number;
  reviewCount?: number;
}

/**
 * Cancellation policy options chosen by the owner at Step 5 (Conditions).
 * Drives the booking-flow UI and the auto-generated PDF contract.
 */
export type CancellationPolicy = 'flexible' | 'moderate' | 'strict';

/**
 * Security deposit collection method chosen by the owner.
 * - 'none'      → no deposit required
 * - 'cash'      → owner collects physically at venue
 * - 'card_hold' → Stripe authorisation hold 48 h before event (not a real charge)
 */
export type DepositMethod = 'none' | 'cash' | 'card_hold';

/**
 * Conditions block — authored in Step 5 of the wizard.
 * Feeds the automated PDF contract generator.
 */
export interface SpaceConditions {
  /** Free-cancellation window in days before the event (1..60). */
  cancellationDays:    number;
  /** @deprecated Replaced by cancellationDays. Kept for backward compatibility. */
  cancellationPolicy?: CancellationPolicy;
  depositMethod:      DepositMethod;
  cleaningMandatory:  boolean;
  /** Minimum age of the contracting party. Default: 25. */
  minAge:             number;
  /** Free-text house rules included verbatim in the PDF contract. */
  houseRules:         string;
  /** Equipment and inventory list included in the contract. */
  inventoryItems?:    string;
}

export const DEFAULT_CONDITIONS: SpaceConditions = {
  cancellationDays:   15,
  depositMethod:      'cash',
  cleaningMandatory:  false,
  minAge:             25,
  houseRules:         '',
  inventoryItems:     '',
};

export type Asset = Space;

/**
 * One-time release of a recurring fixed-block run on a specific date.
 * Owner-initiated; makes the underlying slot available for bookings.
 * Lives under /assets/{assetId}/scheduleExceptions/{id}.
 */
export interface ScheduleException {
  id?: string;
  /** 'YYYY-MM-DD' — the specific date being released */
  date: string;
  /** 'HH:MM' — inclusive start of the released range */
  startTime: string;
  /** 'HH:MM' — exclusive end of the released range */
  endTime: string;
  /** Optional label from the original fixed block, for traceability */
  originalLabel?: string;
  releasedBy: string;
  /** Server timestamp set on create */
  releasedAt?: unknown;
}